import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { getComicById, getRecommendedComics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');
  const recommended = comic ? getRecommendedComics(comic.id, 6) : [];

  if (!comic) {
    return (
      <>
        <SEOHead title="漫画未找到 - 可乐漫画" description="您要找的漫画不存在或已被下架。" />
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
                <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
              </div>
              <h1 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h1>
              <p className="text-sm text-foreground-500 mb-6">您要找的漫画不存在或已被下架。</p>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium cursor-pointer whitespace-nowrap">
                <i className="ri-home-line"></i>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${comic.title} - 可乐漫画 | ${comic.author} | ${comic.category}漫画免费在线看`}
        description={`${comic.title}——${comic.description.slice(0, 120)}... 可乐漫画提供${comic.title}全集免费在线观看，高清画质，流畅阅读。`}
        keywords={`${comic.title}, ${comic.author}, ${comic.category}, ${comic.tags.join(', ')}, 在线看漫画, 可乐漫画`}
        canonicalUrl={`https://kelecomic.com/comic/${comic.id}`}
        ogImage={comic.cover}
        ogType="article"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: comic.title,
          description: comic.description,
          author: { '@type': 'Person', name: comic.author },
          datePublished: comic.createdAt,
          dateModified: comic.updatedAt,
          publisher: {
            '@type': 'Organization',
            name: '可乐漫画',
          },
        }}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: comic.category, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]} />

            <article className="py-8">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-shrink-0">
                  <div className="w-56 md:w-64 aspect-[2/3] rounded-xl overflow-hidden">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - ${comic.category}漫画封面`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                      comic.status === '连载中' ? 'bg-accent-500 text-white' : 'bg-secondary-500 text-white'
                    }`}>
                      {comic.status}
                    </span>
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-background-200 text-foreground-600 whitespace-nowrap">
                      {comic.category}
                    </span>
                    {comic.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-500 whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">{comic.title}</h1>
                  <p className="text-sm text-foreground-500 mb-4">作者：{comic.author}</p>

                  <div className="flex items-center gap-6 mb-6">
                    <div className="flex items-center gap-1">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500"></i>
                      </div>
                      <span className="text-lg font-semibold text-foreground-900">{comic.rating}</span>
                      <span className="text-xs text-foreground-400">分</span>
                    </div>
                    <div className="flex items-center gap-1 text-foreground-500 text-sm">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-eye-line"></i>
                      </div>
                      {(comic.views / 10000).toFixed(0)}万阅读
                    </div>
                    <div className="flex items-center gap-1 text-foreground-500 text-sm">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-file-list-3-line"></i>
                      </div>
                      {comic.chapters.length}话
                    </div>
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-6 max-w-2xl">
                    {comic.description}
                  </p>

                  <div className="flex items-center gap-3 flex-wrap">
                    <Link
                      to={`/comic/${comic.id}/read/1`}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-play-circle-line"></i>
                      开始阅读
                    </Link>
                    <Link
                      to={`/comic/${comic.id}/read/${comic.chapters.length}`}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-200 hover:bg-background-300 text-foreground-700 font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-skip-forward-line"></i>
                      最新话
                    </Link>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <h2 className="text-lg font-heading text-foreground-900 mb-4">章节列表</h2>
                <div className="bg-background-100 rounded-xl p-4 max-h-96 overflow-y-auto">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    {comic.chapters.map((ch) => (
                      <Link
                        key={ch.id}
                        to={`/comic/${comic.id}/read/${ch.number}`}
                        className="px-4 py-2.5 rounded-lg bg-background-50 hover:bg-primary-50 text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap text-center"
                      >
                        {ch.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </article>

            {recommended.length > 0 && (
              <section className="py-10 border-t border-background-200">
                <h3 className="text-lg font-heading text-foreground-900 mb-6">猜你喜欢</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                  {recommended.map((rc) => (
                    <ComicCard key={rc.id} comic={rc} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}